import { createContext, useContext, useEffect, useState } from 'react'
import * as storage from '@/lib/safeStorage'
import { withViewTransition } from '@/lib/viewTransition'

type ViewMode = 'day' | 'week'

interface ViewModeContextValue {
  viewMode: ViewMode
  setViewMode: (m: ViewMode) => void
  toggleViewMode: () => void
}

const ViewModeContext = createContext<ViewModeContextValue | null>(null)
const STORAGE_KEY = 'tourflow-view-mode'

function getInitial(): ViewMode {
  if (typeof window === 'undefined') return 'day'
  const stored = storage.getItem(STORAGE_KEY) as ViewMode | null
  if (stored === 'day' || stored === 'week') return stored
  // Default: week on desktop (lg+ = 1024px), day on mobile
  return window.matchMedia('(min-width: 1024px)').matches ? 'week' : 'day'
}

export function ViewModeProvider({ children }: { children: React.ReactNode }) {
  const [viewMode, setViewModeState] = useState<ViewMode>(getInitial)

  useEffect(() => {
    storage.setItem(STORAGE_KEY, viewMode)
  }, [viewMode])

  return (
    <ViewModeContext.Provider
      value={{
        viewMode,
        setViewMode: (m: ViewMode) => withViewTransition(() => setViewModeState(m)),
        toggleViewMode: () =>
          withViewTransition(() => setViewModeState(m => m === 'day' ? 'week' : 'day')),
      }}
    >
      {children}
    </ViewModeContext.Provider>
  )
}

export function useViewMode() {
  const ctx = useContext(ViewModeContext)
  if (!ctx) throw new Error('useViewMode must be used inside ViewModeProvider')
  return ctx
}
